import moment from 'moment'

// Field catalog for step 1 of the Transfer Incident Data modal. Each entry pairs the
// backend column name — what the mutation's field_overrides expects — with the camelCase
// GraphQL field and enough type information to compare and render the two values.
// The backend counterpart is MERGEABLE_INCIDENT_FIELDS in incidents/merge_fields.py.

export const SOURCE = "SOURCE"
export const DESTINATION = "DESTINATION"

// car_condition is intentionally absent: the damages-driven auto-assign in
// TransferIncidentDataMutation stays its only writer.
export const MERGE_FIELDS = [
    { key: "incident_time", gqlKey: "incidentTime", label: "Incident Time", type: "time" },
    { key: "notification_date", gqlKey: "notificationDate", label: "Notification Date", type: "datetime" },
    { key: "description", gqlKey: "description", label: "Description", type: "text", long: true },
    { key: "reported_by", gqlKey: "reportedBy", label: "Reported By", type: "enum", choices: "reportedByChoices" },
    { key: "branch", gqlKey: "branch", label: "Branch", type: "branch" },
    { key: "rental", gqlKey: "rental", label: "Rental", type: "rental" },
    { key: "incident_type", gqlKey: "incidentType", label: "Incident Type", type: "enum", choices: "incidentTypeChoices" },
    { key: "status", gqlKey: "status", label: "Status", type: "enum", choices: "statusChoices" },
    { key: "source", gqlKey: "source", label: "Source", type: "enum", choices: "sourceChoices" },
    { key: "responsible_party", gqlKey: "responsibleParty", label: "Responsible Party", type: "enum", choices: "responsiblePartyChoices" },
    { key: "has_liability_claim", gqlKey: "hasLiabilityClaim", label: "Has Liability Claim", type: "yesNo" },
    { key: "has_subro_claim", gqlKey: "hasSubroClaim", label: "Has Subro Claim", type: "yesNo" },
    { key: "has_police_report", gqlKey: "hasPoliceReport", label: "Has Police Report", type: "yesNo" },
    { key: "has_dashcam_videos", gqlKey: "hasDashcamVideos", label: "Has Dashcam Videos", type: "yesNo" },
    { key: "has_other_parties", gqlKey: "hasOtherParties", label: "Has Other Parties", type: "yesNo" },
    { key: "car_on_hold", gqlKey: "carOnHold", label: "Car On Hold", type: "boolean" },
    { key: "car_totaled", gqlKey: "carTotaled", label: "Car Totaled", type: "yesNo" },
    { key: "rideshare_platform", gqlKey: "ridesharePlatform", label: "Rideshare Platform", type: "enum", choices: "ridesharePlatformChoices" },
    { key: "rideshare_period", gqlKey: "ridesharePeriod", label: "Rideshare Period", type: "enum", choices: "ridesharePeriodChoices" },
    { key: "rideshare_period_source", gqlKey: "ridesharePeriodSource", label: "Rideshare Period Source", type: "enum", choices: "ridesharePeriodSourceChoices" },
    { key: "incident_state", gqlKey: "incidentState", label: "Incident State", type: "text" },
    { key: "location", gqlKey: "location", label: "Location", type: "text" },
    { key: "is_record_only", gqlKey: "isRecordOnly", label: "Record Only", type: "boolean" },
    { key: "notes", gqlKey: "notes", label: "Notes", type: "text", long: true },
]

// Enum labels come from schema introspection, matching the pattern already used in
// OverviewTab.js and IncidentsList.js. The has_* and car_totaled columns are plain
// YES/NO, so they need no introspection.
export const ENUM_CHOICE_TYPES = [
    { alias: "reportedByChoices", typeName: "IncidentsIncidentReportedByChoices" },
    { alias: "incidentTypeChoices", typeName: "IncidentsIncidentIncidentTypeChoices" },
    { alias: "statusChoices", typeName: "IncidentsIncidentStatusChoices" },
    { alias: "sourceChoices", typeName: "IncidentsIncidentSourceChoices" },
    { alias: "responsiblePartyChoices", typeName: "IncidentsIncidentResponsiblePartyChoices" },
    { alias: "ridesharePlatformChoices", typeName: "IncidentsIncidentRidesharePlatformChoices" },
    { alias: "ridesharePeriodChoices", typeName: "IncidentsIncidentRidesharePeriodChoices" },
    { alias: "ridesharePeriodSourceChoices", typeName: "IncidentsIncidentRidesharePeriodSourceChoices" },
]

// The selection set for one incident's merge fields, shared by both aliases in the query.
export const MERGE_FIELD_GQL = `
    id
    pk
    title
    # Shown read-only at the top of step 1 so the reader can see which incident
    # is older before choosing a direction. Deliberately absent from
    # MERGE_FIELDS: it is context, not a value anyone can merge.
    dateAdded
    incidentDate
    incidentTime
    notificationDate
    description
    reportedBy
    branch { id name }
    rental { id pk startDate }
    incidentType
    status
    source
    responsibleParty
    hasLiabilityClaim
    hasSubroClaim
    hasPoliceReport
    hasDashcamVideos
    hasOtherParties
    carOnHold
    carTotaled
    ridesharePlatform
    ridesharePeriod
    ridesharePeriodSource
    incidentState
    location
    isRecordOnly
    metadata
    notes
`

// Metadata rows a merge may offer. An import reference is a title/id pair that only
// makes sense together — taking one incident's Monday title but the other's row id
// would point the survivor at a row that does not describe it — so each pair is one
// row that moves both keys as a unit. `fields` lists the underlying metadata keys,
// human-readable one first; every one must appear in MERGEABLE_METADATA_KEYS in
// incidents/merge_fields.py or the backend rejects it.
//
// Deliberately absent: crm_notes (owned by the step 2 checkbox), archive_reason
// (an archival, not a merge), and automated_tasks (task-id bookkeeping — copying
// it would point the survivor's records at another incident's task rows).
const METADATA_GROUPS = [
    { key: "crm", label: "CRM Data Import Reference", fields: ["crm_name", "crm_id"] },
    { key: "monday", label: "Monday Data Import Reference", fields: ["monday_row_title", "monday_row_id"] },
    {
        key: "monday_secondary",
        label: "Monday Data Import Reference (Secondary)",
        fields: ["monday_row_title_1", "monday_row_id_1"],
    },
    { key: "buggy_gs", label: "Google Sheet Import Reference", fields: ["Buggy_GS"] },
    { key: "comm_pref", label: "Communication Preference", fields: ["comm_pref"] },
    { key: "rideshare_incident_id", label: "Rideshare Incident ID", fields: ["rideshare_incident_id"] },
]

const METADATA_GROUP_PREFIX = "metadata_group."

// metadata keys holding a dict, offered one sub-key at a time so the sibling
// sub-keys on the destination survive. Mirrors NESTED_METADATA_KEYS on the backend.
// Labelled with the literal path so it is unambiguous which id is being replaced.
const NESTED_METADATA_KEYS = [
    { key: "external_ids", label: (subKey) => `external_ids.${subKey}` },
    { key: "additional_info", label: (subKey) => `additional_info.${subKey}` },
]

export function parseMetadata(metadata) {
    if (!metadata) return {}
    if (typeof metadata !== "string") return metadata
    try {
        return JSON.parse(metadata) || {}
    } catch {
        return {}
    }
}

function normalize(value) {
    if (value === undefined || value === null) return null
    if (typeof value === "string" && value.trim() === "") return null
    return value
}

function isPlainObject(value) {
    return !!value && typeof value === "object" && !Array.isArray(value)
}

export function readFieldValue(incident, field) {
    if (!incident || !field) return null
    if (field.type === "branch") return normalize(incident.branch?.id)
    if (field.type === "rental") return normalize(incident.rental?.id)
    return normalize(incident[field.gqlKey])
}

export function formatFieldValue(incident, field, choiceMaps = {}) {
    const value = readFieldValue(incident, field)
    if (value === null) return null

    switch (field.type) {
        case "time":
            return moment(value, "HH:mm:ss").format("h:mm A")
        case "datetime":
            return moment(value).format("MM/DD/YYYY h:mm A")
        case "boolean":
            return value ? "Yes" : "No"
        case "yesNo":
            return value === "YES" ? "Yes" : "No"
        case "enum": {
            const choices = choiceMaps[field.choices]?.enumValues || []
            return choices.find((item) => item.name === value)?.description || String(value)
        }
        case "branch":
            return incident.branch?.name || `#${value}`
        case "rental": {
            const startDate = incident.rental?.startDate
            const started = startDate ? ` · started ${moment.utc(startDate).format("MM/DD/YYYY")}` : ""
            return `#${incident.rental?.pk ?? value}${started}`
        }
        default:
            return String(value)
    }
}

// metadata values are untyped JSON, so format by runtime type: booleans read as
// Yes/No like the rest of the modal, and a stray object stringifies to something
// legible rather than "[object Object]".
function formatMetadataValue(value) {
    if (value === null) return null
    if (typeof value === "boolean") return value ? "Yes" : "No"
    if (typeof value === "object") return JSON.stringify(value)
    return String(value)
}

function buildRow(key, label, fromValue, toValue) {
    return {
        key,
        label,
        long: false,
        fromValue,
        toValue,
        fromIsEmpty: fromValue === null,
        toIsEmpty: toValue === null,
        fromDisplay: formatMetadataValue(fromValue),
        toDisplay: formatMetadataValue(toValue),
    }
}

// An import-reference row stands for several metadata keys at once. It shows the
// readable name with its id alongside, and compares on every member key so the row
// surfaces whenever any part of the reference differs.
function buildGroupRow(group, fromMeta, toMeta) {
    const read = (meta) => group.fields.map((field) => normalize(meta[field]))
    const fromValues = read(fromMeta)
    const toValues = read(toMeta)

    const display = (values) => {
        const [name, ...rest] = values
        const ids = rest.filter((value) => value !== null).map(formatMetadataValue)
        const parts = []
        if (name !== null) parts.push(formatMetadataValue(name))
        if (ids.length) parts.push(parts.length ? `(${ids.join(" · ")})` : ids.join(" · "))
        return parts.length ? parts.join(" ") : null
    }

    const allEmpty = (values) => values.every((value) => value === null)

    return {
        key: `${METADATA_GROUP_PREFIX}${group.key}`,
        label: group.label,
        long: false,
        // Compared as a unit: the row differs if any member key differs.
        fromValue: JSON.stringify(fromValues),
        toValue: JSON.stringify(toValues),
        // The reference as a whole is missing only when none of its keys are set.
        fromIsEmpty: allEmpty(fromValues),
        toIsEmpty: allEmpty(toValues),
        fromDisplay: display(fromValues),
        toDisplay: display(toValues),
    }
}

function metadataRows(fromIncident, toIncident) {
    const fromMeta = parseMetadata(fromIncident?.metadata)
    const toMeta = parseMetadata(toIncident?.metadata)
    const rows = []

    // Nested groups expand to the union of sub-keys present on either side, so a
    // key only one incident carries still gets a row.
    NESTED_METADATA_KEYS.forEach(({ key: group, label }) => {
        const fromGroup = isPlainObject(fromMeta[group]) ? fromMeta[group] : {}
        const toGroup = isPlainObject(toMeta[group]) ? toMeta[group] : {}
        const subKeys = Array.from(new Set([...Object.keys(fromGroup), ...Object.keys(toGroup)]))
            .filter(Boolean)
            // A dot would make the path ambiguous and the backend would reject it.
            .filter((subKey) => !subKey.includes("."))
            .sort()

        subKeys.forEach((subKey) => {
            rows.push(
                buildRow(
                    `metadata.${group}.${subKey}`,
                    label(subKey),
                    normalize(fromGroup[subKey]),
                    normalize(toGroup[subKey])
                )
            )
        })
    })

    METADATA_GROUPS.forEach((group) => {
        rows.push(buildGroupRow(group, fromMeta, toMeta))
    })

    return rows
}

// A row is only worth a decision when the source could actually contribute
// something. If the source is empty and the destination is not, taking the source
// would only erase data, so the row is settled and folds in with the matches.
function needsDecision(row) {
    if (row.fromValue === row.toValue) return false
    if (row.fromIsEmpty && !row.toIsEmpty) return false
    return true
}

export function diffIncidents(fromIncident, toIncident, choiceMaps = {}) {
    const differing = []
    const identical = []

    const rows = MERGE_FIELDS.map((field) => {
        const fromValue = readFieldValue(fromIncident, field)
        const toValue = readFieldValue(toIncident, field)
        return {
            key: field.key,
            label: field.label,
            long: !!field.long,
            fromValue,
            toValue,
            fromIsEmpty: fromValue === null,
            toIsEmpty: toValue === null,
            fromDisplay: formatFieldValue(fromIncident, field, choiceMaps),
            toDisplay: formatFieldValue(toIncident, field, choiceMaps),
        }
    }).concat(metadataRows(fromIncident, toIncident))

    rows.forEach((row) => {
        if (needsDecision(row)) differing.push(row)
        else identical.push(row)
    })

    return { differing, identical }
}

export function defaultResolutions(rows) {
    return (rows || []).reduce((acc, row) => ({ ...acc, [row.key]: DESTINATION }), {})
}

export function buildFieldOverrides(resolutions) {
    return Object.keys(resolutions || {})
        .filter((key) => resolutions[key] === SOURCE)
        // An import-reference row is one choice for the user but several metadata
        // keys on the wire; the backend has no notion of the grouping.
        .flatMap((key) => {
            if (!key.startsWith(METADATA_GROUP_PREFIX)) return [{ field: key }]
            const group = METADATA_GROUPS.find(
                (candidate) => `${METADATA_GROUP_PREFIX}${candidate.key}` === key
            )
            return group ? group.fields.map((field) => ({ field: `metadata.${field}` })) : []
        })
}

// Taking the source's time on a shared date makes the two records collide on
// (car, incident_date, incident_time). That is allowed mid-merge, but worth flagging.
export function hasTimeCollision(fromIncident, toIncident, resolutions) {
    if ((resolutions || {}).incident_time !== SOURCE) return false
    if (!fromIncident?.incidentDate || !toIncident?.incidentDate) return false
    return fromIncident.incidentDate === toIncident.incidentDate
}
